import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { ViewerDocument } from '@sandtech-hub-monorepo/ehub-client';

import { GsaEnrollment } from '../../data/gsaEnrollments';
import { useSubscriptionState } from '../../data/useSubscriptionState';
import { AppRoutesPaths, subscribePath } from '../../routes/types';
import { EnrolledProgramCard } from './EnrolledProgramCard';

interface Props {
  enrollments: GsaEnrollment[];
}

/**
 * The learner's in-progress programs, as the Home dashboard and the Learning
 * "Current" tab both render them. One component because the two were identical
 * down to the toast stubs, and the next paywall-copy change should not have to
 * be made twice.
 *
 * Locks are per program and come off the enrollment; whether the learner has
 * lost access at all is account-wide, which is why that one is read here.
 */
export const EnrolledProgramList = ({ enrollments }: Props) => {
  const navigate = useNavigate();
  const { state } = useSubscriptionState();

  /*
   * Read once here rather than per card: Savanna scopes a curriculum flow to
   * a learner, so every card needs the same sandId. Cache-first, and every
   * route into this list has already loaded the viewer.
   */
  const { data } = useQuery(ViewerDocument, { fetchPolicy: 'cache-first' });
  const sandId = data?.viewer?.sandId;

  // The cards render a per-learner curriculum, so there is nothing to show
  // until the viewer resolves.
  if (!sandId) {
    return null;
  }

  /*
   * `ended` only. `blocked` covers statuses that never entitled as well as
   * ones that did, so it cannot be read as "lost access" — a learner whose
   * first payment was declined was being shown "Access Expired" on a program
   * whose paid modules had never opened. The banner above this list says what
   * is wrong in either case, and says it accurately per state, so nothing is
   * lost by keeping this label to the one meaning it has.
   */
  const lostAccess = state === 'ended';

  return (
    <>
      {enrollments.map((enrollment) => (
        <EnrolledProgramCard
          key={enrollment.id}
          program={enrollment.program}
          programId={enrollment.programId}
          sandId={sandId}
          completedCourseIds={enrollment.completedCourseIds}
          paidUnlocked={enrollment.covered}
          expired={lostAccess && !enrollment.covered}
          onOpenProgram={() =>
            navigate(
              AppRoutesPaths.Program.replace(
                ':programId',
                enrollment.programAbbreviation
              )
            )
          }
          onSubscribe={() => navigate(subscribePath(enrollment.programId))}
          onGoToCommunity={() => navigate(AppRoutesPaths.Community)}
        />
      ))}
    </>
  );
};
